import { RouteModule } from '#/horizon/components/RouteModule';
import { setSurrogate } from '#/horizon/middlewares/surrogate.middleware';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { DynamicSearchInternshipRefinements } from '#/modules/search/internships/components/Refinements/DynamicSearchInternshipRefinements';
import { searchInternshipsPathByLocale } from '#/modules/search/internships/routing/searchInternships.paths';
import { searchInternshipsPathReference } from '#/modules/search/routing/search.paths';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { RoutePathLoaderArgs } from '#/routing/classes/types/RoutePathLoaderArgs';
import type { RouteProps } from '#/routing/types/RouteProps';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchInternshipsPage } from './SearchInternships.page';

export const handle = {
  searchRefinements: <DynamicSearchInternshipRefinements />,
} satisfies LayoutHandleElements;

export function loader({
  url,
  context,
}: RoutePathLoaderArgs<typeof searchInternshipsPathReference>) {
  setSurrogate({ context, data: { type: 'hardcoded-route' } });
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchInternshipsPathByLocale,
      darkBackground: true,
      defaultSearchType: 'INTERNSHIP',
      hideFooterDivider: true,
      searchFromPageType: 'SEARCH',
      showDesktopSearch: true,
      isSearchPage: true,
      hideMobileHeaderOnScroll: true,
      showSearchRefinements: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      pathByLocale: searchInternshipsPathByLocale,
      referenceRoute: searchInternshipsPathReference,
    }),
    serverNow: serverContext.now,
  };
}

export default function SearchInternshipsRoute({
  loaderData: { linkRels, serverNow },
}: RouteProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-internships.jpg');
      case I18nLocale.ES:
        return;
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: internship search page title */
        i18n('Best Internships Near You, Remote Internships')
      }
      description={
        /* TRANSLATORS: internship search page description */
        i18n('Internships with leading nonprofits and social-impact companies')
      }
      linkRels={linkRels}
      ogImage={ogImage}
    >
      <SearchInternshipsPage serverNow={serverNow} />
    </RouteModule>
  );
}
